import { h, svgUse } from '../utils/dom.js';
import { t, getLang } from '../i18n/index.js';
import { api } from '../api/client.js';
import { ENDPOINTS } from '../api/endpoints.js';
import { emit, EVENT } from '../utils/events.js';
import { consumePendingScenario, setPendingScenario } from '../utils/sessionStore.js';
import { Card } from '../components/common/Card.js';
import { SectionTitle } from '../components/common/SectionTitle.js';
import { Loader } from '../components/common/Loader.js';
import { ErrorState } from '../components/common/ErrorState.js';

export function SessionPage() {
  const root = h('div', { class: 'page active' });
  const selectedId = consumePendingScenario();

  if (!selectedId) {
    root.appendChild(
      Card(
        SectionTitle({ icon: 'ic-books', title: t('sessionEmptyTitle') }),
        h('p', { style: { marginTop: '12px' } }, t('sessionEmptyDesc')),
        h('button', {
          class: 'edit-btn',
          style: { marginTop: '14px' },
          onclick: () => emit(EVENT.TAB_REQUESTED, 'scenarios'),
        }, t('backToScenarios')),
      )
    );
    root.appendChild(h('p', { class: 'tagline-strip' }, t('tagline')));
    return root;
  }

  root.appendChild(Loader());
  startSession(root, selectedId);
  return root;
}

async function startSession(root, scenarioId) {
  try {
    const session = await api.post(ENDPOINTS.session.start(), {
      student_id: 'anonymous',
      scenario_id: scenarioId,
    });
    renderStage(root, session, scenarioId);
  } catch (err) {
    root.innerHTML = '';
    root.appendChild(ErrorState({
      message: err.message,
      onRetry: () => {
        root.innerHTML = '';
        root.appendChild(Loader());
        setPendingScenario(scenarioId);
        startSession(root, scenarioId);
      },
    }));
  }
}

function renderStage(root, data, scenarioId) {
  root.innerHTML = '';
  const lang = getLang();

  root.appendChild(
    h('div', { class: 'banner' },
      h('h1', {}, data.title_ar || data.title_en || t('sessionTitle')),
      h('p', {}, t('stageLabel') + ': ' + stageName(data.stage, lang)),
    )
  );

  if (data.stage === 'socratic') {
    root.appendChild(Card(
      SectionTitle({ icon: 'ic-docsearch', title: t('contextLabel') }),
      h('p', { style: { marginTop: '12px' } }, data.scenario_context || ''),
      SectionTitle({ icon: 'ic-target', title: t('challengeLabel') }),
      h('p', { style: { marginTop: '12px' } }, data.challenge || ''),
      SectionTitle({ icon: 'ic-bulb', title: t('questionLabel') }),
      h('p', { style: { marginTop: '12px' } }, data.socratic_question || ''),
      responseForm(root, data.session_id, scenarioId),
    ));
  }

  if (data.stage === 'diagnosis') {
    root.appendChild(Card(
      SectionTitle({ icon: 'ic-eye', title: t('microLessonTitle') }),
      h('p', { style: { marginTop: '12px' } }, data.micro_lesson || t('correctNoLesson')),
      SectionTitle({ icon: 'ic-play', title: t('reChallengeLabel') }),
      h('p', { style: { marginTop: '12px' } }, (data.re_challenge.scenario_context || '') + '\n' + (data.re_challenge.challenge || '')),
      responseForm(root, data.session_id, scenarioId),
    ));
  }

  if (data.stage === 'final') {
    const mastered = !!data.mastered;
    root.appendChild(Card(
      h('div', { class: 'badge-icon ' + (mastered ? 'bg-green' : 'bg-purple') }, svgUse(mastered ? 'ic-star' : 'ic-play')),
      h('h3', { style: { marginTop: '12px' } }, mastered ? t('masteredTitle') : t('retryTitle')),
      h('p', { style: { marginTop: '10px' } }, data.message || ''),
      h('div', { style: { marginTop: '14px', display: 'flex', gap: '10px' } },
        h('button', { class: 'edit-btn', onclick: () => emit(EVENT.TAB_REQUESTED, 'scenarios') }, t('backToScenarios')),
        !mastered ? h('button', { class: 'edit-btn', onclick: () => startSession(root, scenarioId) }, t('tryAgain')) : null,
      ),
    ));
  }

  root.appendChild(h('p', { class: 'tagline-strip' }, t('tagline')));
}

function responseForm(root, sessionId, scenarioId) {
  const area = h('textarea', {
    placeholder: t('answerPlaceholder'),
    rows: '4',
    style: { width: '100%', marginTop: '14px', padding: '12px', borderRadius: '12px', border: '1px solid var(--line)' },
  });
  const btn = h('button', { class: 'edit-btn', style: { marginTop: '12px' } }, svgUse('ic-send'), h('span', {}, t('submitBtn')));
  const wrap = h('div', { style: { marginTop: '14px' } }, area, btn);

  btn.addEventListener('click', async () => {
    const text = area.value.trim();
    if (!text) return;
    btn.disabled = true;
    area.disabled = true;
    btn.querySelector('span').textContent = t('submitting');
    try {
      const res = await api.post(ENDPOINTS.session.respond(), { session_id: sessionId, response_text: text });
      renderStage(root, res, scenarioId);
    } catch (err) {
      btn.disabled = false;
      area.disabled = false;
      btn.querySelector('span').textContent = t('submitBtn');
      wrap.appendChild(h('p', { style: { color: 'var(--red)', marginTop: '8px' } }, err.message));
    }
  });

  return wrap;
}

function stageName(stage, lang) {
  const map = {
    socratic: { ar: 'سؤال استكشافي', en: 'Socratic' },
    diagnosis: { ar: 'التشخيص', en: 'Diagnosis' },
    final: { ar: 'النتيجة', en: 'Result' },
  };
  return (map[stage] || {})[lang] || stage || '';
}
